import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import { formatClassTime } from '../utils/classModalHelpers';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export default function RegistrationLinkGenerator({ 
    open, 
    onClose, 
    classData,
    subdomain 
}) {
    const [copySuccess, setCopySuccess] = useState(false);

    // Generate the registration URL
    const generateRegistrationUrl = () => {
        if (!classData || !subdomain) return '';
        
        // Determine if we're in development or production
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname.includes('localhost');
        
        let baseUrl;
        if (isDevelopment) {
            // For development, use localhost with port
            baseUrl = `http://${subdomain}.localhost:3000`;
        } else {
            // For production
            baseUrl = `https://${subdomain}.irontrack.ee`;
        }
        
        // Add class ID and date as query parameters
        const classDate = new Date(classData.time).toISOString();
        return `${baseUrl}/?register=${classData.id}&classDate=${encodeURIComponent(classDate)}`;
    };

    const registrationUrl = generateRegistrationUrl();

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(registrationUrl);
            setCopySuccess(true);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleOpenInNewTab = () => {
        window.open(registrationUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Guest Registration Link
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {classData && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1, color: 'white' }}>
                                <Typography variant="h6" gutterBottom>
                                    {classData.trainingName}
                                </Typography>
                                <Typography variant="body2">
                                    {formatClassTime(classData.time)}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                                    Free Class - Guest Registration Available
                                </Typography>
                            </Box>
                        )}

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Share this link with guests so they can register directly for this class:
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <TextField
                                fullWidth
                                value={registrationUrl}
                                multiline
                                maxRows={3}
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        fontFamily: 'monospace',
                                        fontSize: '0.9rem',
                                        bgcolor: 'grey.100'
                                    }
                                }}
                            />
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <IconButton
                                    color="primary"
                                    onClick={handleCopyToClipboard}
                                    title="Copy to clipboard"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'primary.main'
                                    }}
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                                
                                <IconButton
                                    color="success"
                                    onClick={handleOpenInNewTab}
                                    title="Open in new tab"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'success.main'
                                    }}
                                >
                                    <OpenInNewIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>How it works:</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                • When guests click this link, they'll be taken directly to your gym's page
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • The registration form will open automatically for this specific class
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • Guests only need to enter their name and email to register
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>
                        Close
                    </Button>
                    <Button 
                        variant="contained" 
                        color="success"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyToClipboard}
                    >
                        Copy Link
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={copySuccess}
                autoHideDuration={3000}
                onClose={() => setCopySuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setCopySuccess(false)} 
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    Registration link copied to clipboard!
                </Alert>
            </Snackbar>
        </>
    );
}

